const HEX = /^#[0-9A-F]{6}$/;

type Rgb = { r: number; g: number; b: number };

function rgb(value: string): Rgb {
  return { r: Number.parseInt(value.slice(1, 3), 16), g: Number.parseInt(value.slice(3, 5), 16), b: Number.parseInt(value.slice(5, 7), 16) };
}

function hex({ r, g, b }: Rgb) {
  return `#${[r, g, b].map((value) => Math.round(value).toString(16).padStart(2, "0")).join("")}`.toUpperCase();
}

function mix(source: Rgb, target: Rgb, amount: number): Rgb {
  return { r: source.r + (target.r - source.r) * amount, g: source.g + (target.g - source.g) * amount, b: source.b + (target.b - source.b) * amount };
}

function luminance(value: Rgb) {
  const channel = (part: number) => { const normalised = part / 255; return normalised <= 0.04045 ? normalised / 12.92 : ((normalised + 0.055) / 1.055) ** 2.4; };
  return 0.2126 * channel(value.r) + 0.7152 * channel(value.g) + 0.0722 * channel(value.b);
}

export function contrastRatio(first: string, second: string) {
  const a = luminance(rgb(first)); const b = luminance(rgb(second));
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

export function normaliseSrgb(value: string) {
  const candidate = value.trim().toUpperCase();
  if (!HEX.test(candidate)) throw new Error("INVALID_COLOUR");
  return candidate;
}

function accessibleAction(raw: string) {
  const source = rgb(raw);
  for (let step = 0; step <= 20; step += 1) {
    const candidate = hex(mix(source, { r: 0, g: 0, b: 0 }, step / 20));
    if (contrastRatio(candidate, "#FFFFFF") >= 4.5) return candidate;
  }
  return "#1F3F46";
}

export type ResolvedBrandTokens = { primary: string; secondary: string; accent: string; primaryText: "#FFFFFF" };

/** Customer colours are inputs; only these bounded, accessible tokens reach UI surfaces. */
export function resolveBrandTokens(primary: string, secondary: string, accent: string): ResolvedBrandTokens {
  const safePrimary = accessibleAction(normaliseSrgb(primary));
  const safeSecondary = hex(mix(rgb(normaliseSrgb(secondary)), { r: 255, g: 255, b: 255 }, 0.86));
  const safeAccent = hex(mix(rgb(normaliseSrgb(accent)), rgb(safePrimary), 0.18));
  return { primary: safePrimary, secondary: safeSecondary, accent: safeAccent, primaryText: "#FFFFFF" };
}
